import { useState } from "react";
import { Share2, Copy, Check, Download, Link2 } from "lucide-react";

function mediaUrl(src) {
  if (!src) return "";
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
}

// Minimal inline brand glyphs (lucide dropped brand icons); kept monochrome to match the UI.
const XIcon = (p) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden {...p}>
    <path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.9l-4.79-6.26L5.8 22H2.54l8.02-9.17L1 2h7.08l4.33 5.72L18.244 2Zm-1.21 18h1.79L7.06 3.9H5.14L17.034 20Z" />
  </svg>
);
const FbIcon = (p) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden {...p}>
    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.9 3.77-3.9 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.44 2.9h-2.34V22c4.78-.79 8.44-4.94 8.44-9.94Z" />
  </svg>
);
const WaIcon = (p) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden {...p}>
    <path d="M12.04 2c-5.5 0-9.96 4.46-9.96 9.96 0 1.76.46 3.48 1.34 5L2 22l5.16-1.35a9.9 9.9 0 0 0 4.88 1.25h.01c5.5 0 9.96-4.46 9.96-9.96 0-2.66-1.04-5.16-2.92-7.04A9.9 9.9 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.06.8.82-2.98-.2-.31a8.2 8.2 0 0 1-1.26-4.37c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.2 8.2 0 0 1 2.41 5.83c0 4.55-3.7 8.25-8.24 8.25Zm4.52-6.17c-.25-.12-1.47-.72-1.69-.8-.23-.09-.39-.13-.56.12-.16.25-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.38-1.72-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.41-.56-.42h-.48c-.16 0-.43.06-.65.31-.22.25-.86.84-.86 2.05 0 1.21.88 2.38 1 2.54.12.16 1.73 2.64 4.19 3.7.59.25 1.04.4 1.4.51.59.19 1.12.16 1.54.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29Z" />
  </svg>
);

export const ShareFeature = ({ shareUrl, title, cardUrl, storyId, variant = "full", eyebrow, heading, subtext, hideCard = false, testidPrefix = "share" }) => {
  const [copied, setCopied] = useState(false);
  const u = encodeURIComponent(shareUrl);
  const t = encodeURIComponent(title || "");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) { /* noop */ }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url: shareUrl }); return; } catch (e) { /* fall through */ }
    }
    copy();
  };

  const btn = "inline-flex items-center gap-2 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] font-bold border border-white/20 text-white hover:border-[#E30613] hover:text-[#E30613] transition-colors";

  if (variant === "compact") {
    return (
      <div className="flex flex-wrap items-center gap-3" data-testid="detail-share-bar">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500 mr-1">Share</span>
        <button onClick={nativeShare} data-testid="detail-share-button" className={btn}>
          {copied ? <Check size={14} /> : <Share2 size={14} />}
          {copied ? "Link Copied" : "Share Story"}
        </button>
        <a href={`https://twitter.com/intent/tweet?text=${t}&url=${u}`} target="_blank" rel="noopener noreferrer" data-testid="detail-share-x" className={btn}>
          <XIcon /> X
        </a>
        {cardUrl && (
          <a href={mediaUrl(cardUrl)} download={`${storyId}-vlmag-share.png`} data-testid="detail-download-card" className={btn}>
            <Download size={14} /> Share Card
          </a>
        )}
      </div>
    );
  }

  return (
    <section
      data-testid={`${testidPrefix}-feature-module`}
      className="relative overflow-hidden border border-[#E30613]/40 bg-gradient-to-br from-[#E30613]/10 via-black to-black p-8 md:p-10"
    >
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
        <div className="max-w-xl">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E30613] mb-3">{eyebrow || "Featured? Take it everywhere"}</p>
          <h3 className="font-display uppercase text-3xl md:text-4xl leading-[0.95] tracking-tight text-white">
            {heading || "Share Your Feature"}
          </h3>
          <p className="text-sm md:text-base text-neutral-400 mt-4 leading-relaxed">
            {subtext || "Drop this link on your socials — one tap and your followers land straight on your story, with a full VLMAG preview card. No searching, no dead ends."}
          </p>

          <div className="mt-6 flex items-stretch gap-2 max-w-md">
            <div className="flex-1 flex items-center gap-2 border border-white/15 bg-black/60 px-3 py-2.5 min-w-0" data-testid={`${testidPrefix}-link-box`}>
              <Link2 size={14} className="text-neutral-500 shrink-0" />
              <span className="text-xs text-neutral-300 truncate">{shareUrl.replace(/^https?:\/\//, "")}</span>
            </div>
            <button
              onClick={copy}
              data-testid={`${testidPrefix}-copy-button`}
              className="shrink-0 inline-flex items-center gap-2 px-4 text-[11px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3 lg:min-w-[240px]">
          <button onClick={nativeShare} data-testid={`${testidPrefix}-native-button`} className="inline-flex items-center justify-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.2em] font-bold bg-white text-black hover:bg-neutral-200 transition-colors">
            <Share2 size={15} /> Share…
          </button>
          <div className="grid grid-cols-3 gap-2">
            <a href={`https://twitter.com/intent/tweet?text=${t}&url=${u}`} target="_blank" rel="noopener noreferrer" data-testid={`${testidPrefix}-x`} title="Share on X" className="flex items-center justify-center gap-2 py-3 text-[11px] uppercase tracking-[0.15em] font-bold border border-white/20 text-white hover:border-[#E30613] hover:text-[#E30613] transition-colors">
              <XIcon /> X
            </a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${u}`} target="_blank" rel="noopener noreferrer" data-testid={`${testidPrefix}-facebook`} title="Share on Facebook" className="flex items-center justify-center py-3 border border-white/20 text-white hover:border-[#E30613] hover:text-[#E30613] transition-colors">
              <FbIcon />
            </a>
            <a href={`https://wa.me/?text=${t}%20${u}`} target="_blank" rel="noopener noreferrer" data-testid={`${testidPrefix}-whatsapp`} title="Share on WhatsApp" className="flex items-center justify-center py-3 border border-white/20 text-white hover:border-[#E30613] hover:text-[#E30613] transition-colors">
              <WaIcon />
            </a>
          </div>
          {cardUrl && !hideCard && (
            <a href={mediaUrl(cardUrl)} download={`${storyId}-vlmag-share.png`} data-testid={`${testidPrefix}-download-card`} className="inline-flex items-center justify-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.2em] font-bold border border-white/20 text-white hover:border-white/60 transition-colors">
              <Download size={15} /> Card for Instagram
            </a>
          )}
        </div>
      </div>
    </section>
  );
};
